/**
 * Admin Live Launch Stats — 50k-user launch dashboard.
 *
 * Polls /api/admin/live-stats every 5 seconds for rolling metrics:
 *   - Active users in last 5 min / 1 hour
 *   - Signups, payments, revenue, generations in last 24h
 *   - Task errors in last 1h
 *   - MRR + subscription mix
 *   - Top referrers
 *
 * Designed to be projected on a screen during launch day so the team can
 * watch the funnel in real time.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity,
  Users,
  DollarSign,
  Zap,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  Pause,
  Play,
  Trophy,
  FlaskConical,
  Filter,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 5000;

const AdminLiveStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [previousStats, setPreviousStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paused, setPaused] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  // Separate state for the A/B experiment table — refreshes every 30s
  // (less frequent than live KPIs since conversion events are slower-moving).
  const [abResults, setAbResults] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const intervalRef = useRef(null);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/live-stats`);
      setPreviousStats((prev) => stats || prev);
      setStats(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  };

  const fetchAbResults = async () => {
    try {
      const r = await axios.get(`${API}/admin/ab/results`);
      setAbResults(r.data?.experiments || null);
    } catch (_) { /* non-fatal */ }
  };

  const fetchFunnel = async () => {
    try {
      const r = await axios.get(`${API}/admin/funnel/stats`);
      setFunnel(r.data);
    } catch (_) { /* non-fatal */ }
  };

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    fetchStats();
    fetchAbResults();
    fetchFunnel();
    const abId = setInterval(fetchAbResults, 30_000);
    const funnelId = setInterval(fetchFunnel, 30_000);
    return () => { clearInterval(abId); clearInterval(funnelId); };
  }, []);

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }
    intervalRef.current = setInterval(fetchStats, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [paused]);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  const delta = (current, previous) => {
    if (previous == null || current == null) return null;
    return current - previous;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="live-stats-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ink-950" />
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="p-8 rounded-2xl bg-red-50 border border-red-200 text-red-800" data-testid="live-stats-error">
        <p className="font-semibold">Couldn&apos;t load live stats</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-live-stats-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-ink-950 tracking-tight flex items-center gap-3">
            <Activity className="w-8 h-8 text-coral-500" />
            Live Launch Stats
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Auto-refreshing every {POLL_INTERVAL_MS / 1000}s · Last update:{" "}
            <span className="text-ink-950 font-medium">
              {lastUpdate ? lastUpdate.toLocaleTimeString() : "—"}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            data-testid="live-stats-pause-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink-950 hover:bg-coral-500 text-white text-xs font-bold uppercase tracking-[0.18em] transition-colors"
          >
            {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            {paused ? "Resume" : "Pause"}
          </button>
          <button
            type="button"
            onClick={fetchStats}
            data-testid="live-stats-refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink-100 hover:bg-ink-200 text-ink-950 text-xs font-bold uppercase tracking-[0.18em] transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Hero KPIs — biggest, most-watched numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="live-kpis">
        <KpiCard
          label="Active now"
          sublabel="last 5 min"
          value={stats?.active_5min || 0}
          icon={Users}
          accent="coral"
          testid="kpi-active-5min"
          delta={delta(stats?.active_5min, previousStats?.active_5min)}
        />
        <KpiCard
          label="Signups"
          sublabel="last 24h"
          value={stats?.signups_24h || 0}
          icon={TrendingUp}
          accent="green"
          testid="kpi-signups"
        />
        <KpiCard
          label="Revenue"
          sublabel="last 24h"
          value={`$${(stats?.revenue_24h || 0).toLocaleString()}`}
          icon={DollarSign}
          accent="amber"
          testid="kpi-revenue"
        />
        <KpiCard
          label="Generations"
          sublabel="last 24h"
          value={stats?.generations_24h || 0}
          icon={Zap}
          accent="violet"
          testid="kpi-generations"
        />
      </div>

      {/* Second row — secondary metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Active · 1h" value={stats?.active_1hour || 0} testid="metric-active-1h" />
        <MetricCard label="Payments · 24h" value={stats?.payments_24h || 0} testid="metric-payments" />
        <MetricCard
          label="Task errors · 1h"
          value={stats?.errors_1h || 0}
          testid="metric-errors"
          warning={(stats?.errors_1h || 0) > 0}
        />
        <MetricCard
          label="MRR"
          value={`$${(stats?.mrr || 0).toLocaleString()}`}
          testid="metric-mrr"
        />
      </div>

      {/* Subscription mix + Top referrers */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div
          className="p-6 rounded-2xl bg-white border border-ink-200"
          data-testid="subscription-mix"
        >
          <h2 className="text-lg font-bold text-ink-950 mb-4">
            Subscription mix
            <span className="ml-2 text-sm font-normal text-ink-500">
              {stats?.total_real_users || 0} real users
            </span>
          </h2>
          <SubscriptionBreakdown subs={stats?.subscriptions} total={stats?.total_real_users || 0} />
        </div>

        <div
          className="p-6 rounded-2xl bg-white border border-ink-200"
          data-testid="top-referrers"
        >
          <h2 className="text-lg font-bold text-ink-950 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Top referrers
          </h2>
          {stats?.top_referrers?.length ? (
            <ul className="space-y-2">
              {stats.top_referrers.map((r, i) => (
                <li
                  key={r.email}
                  data-testid={`referrer-${i}`}
                  className="flex items-center justify-between p-3 rounded-xl bg-ink-50 hover:bg-ink-100 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-950 truncate">
                      {r.name || r.email}
                    </p>
                    <p className="text-xs text-ink-500 truncate">{r.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-coral-500">
                      {r.referrals_count} refs
                    </p>
                    <p className="text-xs text-ink-500">
                      {r.referrals_tokens_earned || 0} tokens earned
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500 py-6 text-center">No referrers yet.</p>
          )}
        </div>
      </div>

      {/* A/B test results — landing-page experiments */}
      <AbResultsPanel results={abResults} onRefresh={fetchAbResults} />

      {/* Conversion funnel */}
      <FunnelPanel funnel={funnel} />
    </div>
  );
};

// ─── Landing-page A/B experiment results ────────────────────────────────
const AbResultsPanel = ({ results, onRefresh }) => {
  if (!results || Object.keys(results).length === 0) return null;
  return (
    <div className="space-y-3" data-testid="ab-results-section">
      <h2 className="text-lg font-bold text-ink-950 flex items-center gap-2">
        <FlaskConical className="w-5 h-5 text-violet-500" />
        Landing-page A/B tests
      </h2>
      {Object.entries(results).map(([expId, exp]) => (
        <AbExperimentCard
          key={expId}
          expId={expId}
          exp={exp}
          onRefresh={onRefresh}
        />
      ))}
    </div>
  );
};

// ─── Single experiment card with promote / reset controls ──────────────
const AbExperimentCard = ({ expId, exp, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const promoted = exp.promoted_variant || null;

  const promote = async (variant) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/ab/promote`, { experiment: expId, variant });
      toast.success(`Promoted "${variant}" — new visitors will all see this copy`);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't promote variant");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/ab/promote/${expId}`);
      toast.success("Experiment reset to random assignment");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset experiment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="p-6 rounded-2xl bg-white border border-ink-200"
      data-testid={`ab-experiment-${expId}`}
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="font-mono text-xs text-ink-500">{expId}</p>
        <div className="flex items-center gap-2">
          {promoted && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-500/10 text-violet-600 text-[10px] font-bold uppercase tracking-[0.18em]"
              data-testid={`ab-promoted-${expId}`}
            >
              <Trophy className="w-3 h-3" />
              shipping: {promoted}
            </span>
          )}
          {!promoted && exp.winner && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-[10px] font-bold uppercase tracking-[0.18em]"
              data-testid={`ab-winner-${expId}`}
            >
              <Trophy className="w-3 h-3" />
              {exp.winner} winning
            </span>
          )}
          {promoted && (
            <button
              type="button"
              onClick={reset}
              disabled={busy}
              data-testid={`ab-reset-${expId}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ink-100 hover:bg-ink-200 text-ink-700 text-[10px] font-bold uppercase tracking-[0.18em] disabled:opacity-50"
            >
              Reset experiment
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(exp.variants).map(([name, m]) => {
          const isWinner = exp.winner === name;
          const isPromoted = promoted === name;
          // A variant is eligible to ship as default once it has ≥30 views.
          // Below that threshold the conversion rate is just noise.
          const eligible = m.views >= (exp.min_sample_size || 30);
          return (
            <div
              key={name}
              data-testid={`ab-variant-${expId}-${name}`}
              className={`p-4 rounded-xl border flex flex-col gap-2 ${
                isPromoted
                  ? "bg-violet-500/5 border-violet-500/40"
                  : isWinner
                  ? "bg-emerald-500/5 border-emerald-500/40"
                  : "bg-ink-50 border-ink-200"
              }`}
            >
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink-700">
                {name}
              </p>
              <p className="text-2xl font-bold text-ink-950 tabular-nums">
                {m.rate_pct}
              </p>
              <p className="text-[11px] text-ink-500">
                {m.conversions} clicks · {m.views} views
              </p>
              {!isPromoted && (
                <button
                  type="button"
                  onClick={() => promote(name)}
                  disabled={busy || !eligible}
                  data-testid={`ab-promote-${expId}-${name}`}
                  title={
                    !eligible
                      ? `Needs ≥${exp.min_sample_size || 30} views to ship as default`
                      : "Lock in this copy for all new visitors"
                  }
                  className={`mt-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-[0.16em] transition-colors ${
                    eligible
                      ? "bg-ink-950 hover:bg-coral-500 text-white"
                      : "bg-ink-100 text-ink-400 cursor-not-allowed"
                  }`}
                >
                  Ship as default
                </button>
              )}
              {isPromoted && (
                <span className="mt-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-[0.16em] bg-violet-500/10 text-violet-600">
                  Currently shipping
                </span>
              )}
            </div>
          );
        })}
      </div>
      {!exp.winner && !promoted && (
        <p className="text-[11px] text-ink-500 mt-3">
          Needs ≥{exp.min_sample_size} views per variant to declare a winner.
        </p>
      )}
    </div>
  );
};

const KpiCard = ({ label, sublabel, value, icon: Icon, accent = "coral", delta, testid }) => {
  const accents = {
    coral: "bg-coral-500/10 text-coral-500 border-coral-500/20",
    green: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    violet: "bg-violet-500/10 text-violet-600 border-violet-500/20",
  };
  return (
    <div
      className={`p-6 rounded-2xl bg-white border border-ink-200 relative overflow-hidden`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-3">
        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${accents[accent]} border`}>
          <Icon className="w-5 h-5" />
        </span>
        {delta != null && delta !== 0 && (
          <span
            className={`text-xs font-bold uppercase tracking-[0.18em] ${
              delta > 0 ? "text-emerald-600" : "text-rose-500"
            }`}
            data-testid={`${testid}-delta`}
          >
            {delta > 0 ? "+" : ""}
            {delta}
          </span>
        )}
      </div>
      <p className="text-3xl font-black text-ink-950 tracking-tight" data-testid={`${testid}-value`}>
        {value}
      </p>
      <p className="text-sm text-ink-700 font-medium mt-1">{label}</p>
      {sublabel && (
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500 mt-0.5">
          {sublabel}
        </p>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, warning, testid }) => (
  <div
    className={`p-5 rounded-2xl border ${
      warning
        ? "bg-red-50 border-red-200"
        : "bg-white border-ink-200"
    }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-1.5">
      {warning && <AlertTriangle className="w-4 h-4 text-red-500" />}
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500">{label}</p>
    </div>
    <p
      className={`text-2xl font-bold ${warning ? "text-red-600" : "text-ink-950"}`}
      data-testid={`${testid}-value`}
    >
      {value}
    </p>
  </div>
);

const SubscriptionBreakdown = ({ subs, total }) => {
  const tiers = [
    { key: "free", label: "Free", color: "bg-ink-400" },
    { key: "starter", label: "Starter", color: "bg-blue-400" },
    { key: "premium", label: "Premium", color: "bg-violet-500" },
    { key: "pro", label: "Pro", color: "bg-fuchsia-500" },
    { key: "platinum", label: "Platinum", color: "bg-amber-500" },
    { key: "ultimate", label: "Ultimate", color: "bg-emerald-500" },
  ];
  return (
    <div className="space-y-3">
      {tiers.map((tier) => {
        const count = subs?.[tier.key] || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={tier.key} data-testid={`sub-row-${tier.key}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-ink-950">{tier.label}</span>
              <span className="text-sm tabular-nums text-ink-700">
                {count} <span className="text-ink-500 text-xs">({pct}%)</span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-ink-100 overflow-hidden">
              <div
                className={`h-full ${tier.color} transition-all`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Conversion funnel panel ────────────────────────────────────────────
const FunnelPanel = ({ funnel }) => {
  if (!funnel || !funnel.stages || funnel.stages.length === 0) return null;
  const niceName = {
    plan_viewed:       "Plan viewed",
    method_selected:   "Method selected",
    checkout_started:  "Checkout started",
    payment_completed: "Payment completed",
  };
  return (
    <div className="space-y-3" data-testid="funnel-section">
      <h2 className="text-lg font-bold text-ink-950 flex items-center gap-2">
        <Filter className="w-5 h-5 text-coral-500" />
        Conversion funnel
        <span className="ml-2 text-xs font-normal text-ink-500">
          last {funnel.window_days || 7} days
        </span>
      </h2>
      <div className="p-6 rounded-2xl bg-white border border-ink-200">
        <div className="space-y-3">
          {funnel.stages.map((s, i) => {
            const width = Math.max(s.from_top_pct, 4); // min 4% so an empty bar still draws
            const isDropOff = i > 0 && s.drop_pct >= 40;
            return (
              <div key={s.stage} data-testid={`funnel-stage-${s.stage}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-ink-950">
                    {niceName[s.stage] || s.stage}
                  </span>
                  <span className="text-sm tabular-nums text-ink-700">
                    {s.count}
                    {i > 0 && (
                      <span className={`ml-2 text-xs ${isDropOff ? "text-rose-500" : "text-ink-500"}`}>
                        ({s.from_prev_pct}% of prev)
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-3 rounded-full bg-ink-100 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      isDropOff
                        ? "bg-gradient-to-r from-amber-400 to-rose-500"
                        : "bg-gradient-to-r from-coral-500 to-violet-500"
                    }`}
                    style={{ width: `${width}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {(funnel.by_method && Object.keys(funnel.by_method).length > 0) && (
          <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-4 border-t border-ink-200">
            <BreakdownList title="Paid by method"  data={funnel.by_method} testid="funnel-by-method" />
            <BreakdownList title="Paid by tier"    data={funnel.by_tier}   testid="funnel-by-tier" />
          </div>
        )}
      </div>
    </div>
  );
};

const BreakdownList = ({ title, data, testid }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500 mb-2">{title}</p>
      <ul className="space-y-1">
        {entries.length === 0 ? (
          <li className="text-xs text-ink-400">No paid conversions yet</li>
        ) : (
          entries.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-sm">
              <span className="text-ink-950 font-medium capitalize">{k}</span>
              <span className="tabular-nums text-ink-700">{v}</span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

export default AdminLiveStatsPage;
